import {Component, OnInit, ViewChild} from '@angular/core';
import {IDialogComponent} from '../../../../../common/dialog/iDialog.component';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ObjectUtils} from '../../../../../../shared/utils/object.utils';
import {DialogWithInput} from '../../../../../common/dialog/dialogWithInput';
import {MedicalMorphologyModel} from '../../model/medicalMorphology.model';
import {
  CreateUpdateDefaultComponent
} from '../../../../../core/component/create-update-default/create-update-default.component';
import {MomentAdapter} from '../../../../../../shared/utils/moment.utils';
import {ConsultationSummaryModel} from '../../../../consultation/consultation/api/model/consultationSummary.model';
import {MedicalMorphologyFindOneByIdService} from '../../service/medicalMorphologyFindOneById.service';
import {MedicalMorphologySaveService} from '../../service/save/medicalMorphologySave.service';
import {MedicalMorphologySavePayload} from '../../service/save/medicalMorphologySave.payload';
import {
  AudioTranscriptionConfig
} from "../../../../../common/audio/transcription/api/component/audio-transcription-v2/audio-transcription-v2.component";
import {
  ConsultationTranscriptionHandler
} from "../../../../consultation/consultation/api/service/transcription/consultationTranscriptionHandler";

export interface DialogInput {
  id?: number;
  consultation?: ConsultationSummaryModel;
  partnerId?: number;
}

export interface DialogResult {
  created: boolean;
  entity: MedicalMorphologyModel;
}

@Component({
  selector: 'app-medical-morphology-save-dialog',
  templateUrl: './medical-morphology-save-dialog.component.html',
  providers: [
      MedicalMorphologyFindOneByIdService,
      MedicalMorphologySaveService,
    ConsultationTranscriptionHandler]
})
export class MedicalMorphologySaveDialogComponent implements
    IDialogComponent<DialogInput, DialogResult>,
    OnInit {
  id: number;
  dialog: DialogWithInput<DialogInput, DialogResult>;
  formGroup: FormGroup;
  audioTranscription: AudioTranscriptionConfig;
  @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;


  constructor(private formBuilder: FormBuilder,
              public findByIdService: MedicalMorphologyFindOneByIdService,
              public saveService: MedicalMorphologySaveService,
              private transcriptionHandler: ConsultationTranscriptionHandler) {
    this.formGroup = this.formBuilder.group({
      id: this.formBuilder.control(undefined),
      date: this.formBuilder.control(MomentAdapter.current().get(), Validators.required),
      height: this.formBuilder.control(undefined),
      weight: this.formBuilder.control(undefined),
      shoeSize: this.formBuilder.control(undefined),
      comment: this.formBuilder.control(undefined),
      consultationId: this.formBuilder.control(undefined),
      partnerId: this.formBuilder.control(undefined),
    });
  }

  ngOnInit(): void {
    this.id = this.dialog.input.id;
    if (! this.id) {
      let context = 'partnerId=' + this.dialog.input.partnerId;
      if (this.dialog.input.consultation) {
        context += '&consultationId=' + this.dialog.input.consultation.id;
      }

      this.transcriptionHandler.setup({
        consultationId: this.dialog.input.consultation?.id,
        partnerId: this.dialog.input.partnerId,
      })
      this.audioTranscription = {
        audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_SPORT',
        context,
        startAutomatically: false,
        enableConversationOption: false,
        showPreview: true,
        handler: this.transcriptionHandler
      };
    }

    if (this.dialog.input.consultation) {
      this.formGroup.patchValue({
        date: this.dialog.input.consultation.date,
        consultationId: this.dialog.input.consultation.id
      });
    } else {
      this.formGroup.patchValue({
        partnerId: this.dialog.input.partnerId
      });
    }
  }

  entitySaved(entity: MedicalMorphologyModel) {
    this.dialog.dialog.close({
      created: !!this.formGroup.get('id').value,
      entity
    });
  }

  entityLoaded(entity: MedicalMorphologyModel) {
    if (entity) {
      this.formGroup.patchValue({
        id: entity.id,
        date: entity.date,
        height: entity.height,
        weight: entity.weight,
        shoeSize: entity.shoeSize,
        comment: entity.comment,
        consultationId: entity.consultation?.id,
        partnerId: entity.consultation ? undefined : entity.partner.id
      });

      let context = 'partnerId=' + entity?.partner?.id;
      if (entity.consultation) {
        context += '&consultationId=' + entity.consultation?.id;
      }

      this.transcriptionHandler.setup({
        consultationId: entity.consultation?.id,
        partnerId: entity?.partner?.id,
      })
      this.audioTranscription = {
        audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_MORPHOLOGY',
        context,
        startAutomatically: false,
        enableConversationOption: false,
        showPreview: true,
        handler: this.transcriptionHandler
      };
    }
  }

  buildPayload = (): any => {
    const payload = ObjectUtils.assign(new MedicalMorphologySavePayload(), this.formGroup.value);
    return payload;
  }

  executeSave() {
    this.createUpdateDefaultComponent.save();
  }

  get dateControl(): FormControl {
    return this.formGroup.get('date') as FormControl;
  }

}
